import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Modal from './Modal'
import { useNetworkAccess } from '../hooks/useNetworkAccess'
import useOrganizations from '../models/useOrganizations'
import { readMyAccess, requestNetworkAccess, setNetworkAccess } from '../lib/nodeAccess'
import {
  NETWORK_ACCESSES,
  NETWORK_ACCESS_HELP,
  NETWORK_ACCESS_LABEL,
  PRIVATE_LABEL,
  keptPrivateSummary,
  networkAccessSummary,
  nodesOfNetwork,
  offerNetworkAsk,
  requestSummary,
  type DirectoryNodeAccess,
  type NamedNode,
  type NetworkAccess as Access,
} from '../utilities/nodeAccess'

type Props = {
  networkId: string
  networkName: string
  /** The network's owner or an Owner/Admin of its org (§ 5 Network). The router checks it again. */
  canChange: boolean
}

type Result = { summary: string; nodes: NamedNode[]; notes: string[]; kept?: { summary: string; nodes: NamedNode[] } }

const messageOf = (error: unknown): string => (error instanceof Error ? error.message : String(error))

/**
 * The network's access (router PERMISSIONS.md § 4.1, D-20, app#375): Org (the default) or Private
 * (you and org admins). With Org, every member of a node's org may USE the network's nodes that
 * follow it, never as root. A node can be set to Private on its own page; it is never more open than
 * its network.
 *
 * ⚠ A change from Private to Org OPENS nodes to the whole org, so it asks first, and its result
 * names every node it opened (from the router's `opened_nodes`). A change to Private names every
 * node it closed.
 *
 * Every member reads the setting. Only the network owner or an org Owner/Admin changes it. The same
 * section asks for use of every node of the network, one request per node, only when the reader
 * cannot use one of them (app#423).
 */
export default function NetworkAccess({ networkId, networkName, canChange }: Readonly<Props>) {
  const [access, setAccess] = useNetworkAccess(networkId)
  const [pending, setPending] = useState<Access | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Result | null>(null)
  const [error, setError] = useState('')
  // What the reader holds on each node of this network (app#423): undefined until the read
  // answers, null when it cannot say. The ask is offered only when there is a node the reader
  // cannot use.
  const [nodes, setNodes] = useState<DirectoryNodeAccess[] | null | undefined>(undefined)
  const organizations = useOrganizations(s => s.organizations)
  const readerOrgIds = useMemo(() => new Set(organizations.map(o => o.id)), [organizations])

  useEffect(() => {
    let live = true
    setNodes(undefined)
    if (!networkName) {
      setNodes(null)
      return
    }
    readMyAccess({ networkName })
      .then(answer => {
        if (live) setNodes(nodesOfNetwork(answer, networkId))
      })
      .catch(e => {
        console.warn('network_directory my_access', networkId, e)
        if (live) setNodes(null)
      })
    return () => {
      live = false
    }
  }, [networkId, networkName])

  const offerAsk = nodes !== undefined && offerNetworkAsk(nodes, readerOrgIds)

  const act = async (action: () => Promise<Result>) => {
    setBusy(true)
    setError('')
    setResult(null)
    try {
      setResult(await action())
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const apply = (next: Access) =>
    act(async () => {
      const answer = await setNetworkAccess(networkId, next)
      if (answer.access) setAccess(answer.access)
      const kept = keptPrivateSummary(answer)
      return {
        summary: networkAccessSummary(answer),
        nodes: answer.access === 'org' ? answer.opened : answer.closed,
        // The page names the nodes it kept private itself. The router's note says the same with
        // tool names, so it is not shown again.
        notes: kept ? answer.notes.filter(note => !note.includes('kept_private_nodes')) : answer.notes,
        ...(kept ? { kept: { summary: kept, nodes: answer.kept } } : {}),
      }
    })

  const ask = () =>
    act(async () => {
      const answer = await requestNetworkAccess(networkId)
      // The request skips the nodes the reader can already use; naming them again says nothing new.
      return { summary: requestSummary(answer.requested, answer.skipped.length, 'network'), nodes: [], notes: [] }
    })

  return (
    <Box>
      <Typography variant="h6">Access</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: 1 }}>
        Whether the nodes of this network are shared with the org. The network owner and the org owners and admins
        set it.
      </Typography>
      {access === null ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          The access of this network is not known.
        </Typography>
      ) : (
        <RadioGroup
          value={access}
          onChange={event => {
            const next = event.target.value as Access
            if (next !== access) setPending(next)
          }}
        >
          {NETWORK_ACCESSES.map(value => (
            <FormControlLabel
              key={value}
              value={value}
              disabled={busy || !canChange}
              control={<Radio size="small" color="secondary" sx={{ paddingTop: 0.25 }} />}
              sx={{ alignItems: 'flex-start', marginLeft: 0, marginRight: 0, marginBottom: 0.5 }}
              label={
                <>
                  <Typography variant="body2">{NETWORK_ACCESS_LABEL[value]}</Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                    {NETWORK_ACCESS_HELP[value]}
                  </Typography>
                </>
              }
            />
          ))}
        </RadioGroup>
      )}
      {!canChange && access !== null && (
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          Only the network owner or an org owner or admin can change this.
        </Typography>
      )}

      {/* The change asks first: Org opens every node that follows the network to the whole org. */}
      <Modal open={pending !== null} onClose={() => setPending(null)} color={pending === 'org' ? 'error.main' : 'primary.main'}>
        <DialogTitle variant="subtitle1" color={pending === 'org' ? 'error' : 'primary'}>
          {pending ? `Set ${networkName} to ${NETWORK_ACCESS_LABEL[pending]}?` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" component="div">
            {pending === 'org'
              ? `${NETWORK_ACCESS_HELP.org} It opens only the nodes you control: each other node that follows the network is set to ${PRIVATE_LABEL}. The result names every node this opens or sets to ${PRIVATE_LABEL}.`
              : `${NETWORK_ACCESS_HELP.private} A member who used a node only through sharing is refused on their next call. The result names every node this closes.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" sx={{ color: 'GrayText' }} onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button
            autoFocus
            variant="contained"
            color={pending === 'org' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() => {
              const next = pending
              setPending(null)
              if (next) void apply(next)
            }}
          >
            Set
          </Button>
        </DialogActions>
      </Modal>

      {offerAsk && (
        <Button variant="text" size="small" disabled={busy} onClick={() => void ask()} sx={{ marginTop: 1 }}>
          Ask to use every node of this network
        </Button>
      )}
      {result && (
        <Alert severity={result.nodes.length ? 'warning' : 'info'} sx={{ marginTop: 1 }} onClose={() => setResult(null)}>
          <Typography variant="body2">{result.summary}</Typography>
          {result.nodes.length > 0 && <NodeList nodes={result.nodes} />}
          {result.kept && (
            <>
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                {result.kept.summary}
              </Typography>
              <NodeList nodes={result.kept.nodes} />
            </>
          )}
        </Alert>
      )}
      {result?.notes.map(note => (
        <Alert key={note} severity="info" sx={{ marginTop: 1 }}>
          {note}
        </Alert>
      ))}
      {error && (
        <Alert severity="error" sx={{ marginTop: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

function NodeList({ nodes }: Readonly<{ nodes: NamedNode[] }>) {
  return (
    <Box component="ul" sx={{ margin: 0, paddingLeft: 2.5 }}>
      {nodes.map(node => (
        <li key={node.nodeId}>
          <Typography variant="code">{node.name || node.nodeId}</Typography>
        </li>
      ))}
    </Box>
  )
}
